import { useNavigate } from 'react-router-dom'
import Page from 'components/global/Page'
import { useApp } from 'store/steps'
import {
    setIsMarchPromoMode,
    setOrderId,
    setPositions,
} from 'store/selfcare-slice'
import {
    useDeleteCartMutation,
    useGetSelfcareCartQuery,
    useListSelfcareOrderPositionsQuery,
} from 'store/api-slice/hooks'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import FullPageLoader from 'components/global/FullPageLoader'
import { useLocation } from 'react-router-dom'
import { setDeliveryType } from 'store/order-slice'
import ServerErrorModal from 'components/global/ServerErrorModal'
import { setIsErrorModalOpened } from 'store/general-slice'
import {
    setIsSelfcareCartCreated,
    setSelfcareCart,
} from 'store/selfcare-cart-slice'
import { HOME_LINK } from 'constants/links'
import { ENV_LOCAL } from 'constants/system'
import { selfcareFirstStep } from 'routes/basenames'
import { catchErrorByStatus } from 'helpers/errors'
import ConfirmationModal from 'components/global/ConfirmationModal'
import { DEFAULT_LANG } from 'constants/i18n'
import MarchPromoRoutes from 'routes/selfcare/MarchPromoRoutes'
import StandardRoutes from 'routes/selfcare/StandardRoutes'
import { SESSION_TYPE_MARCH_PROMO } from 'constants/app'

const Selfcare = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { pathname } = useLocation()
    const { language } = useApp()

    const { isCartCreated } = useAppSelector((state) => state.selfcareCart)

    const [isMarchPromoSession, setIsMarchPromoSession] = useState(false)

    const isPathForLocalhostModal = pathname.includes('/modal')
    const isDevelopmentMode = process.env.REACT_APP_ENV === ENV_LOCAL

    const {
        isFetching: isCartFetching,
        isError: isCartError,
        data: cartData,
        error: cartError,
    } = useGetSelfcareCartQuery({ _locale: language ?? DEFAULT_LANG })

    const {
        isLoading: arePositionsLoading,
        isError: isPositionsError,
        data: positionsData,
        error: positionsError,
    } = useListSelfcareOrderPositionsQuery({
        _locale: language ?? DEFAULT_LANG,
    })

    const [deleteCart] = useDeleteCartMutation()

    const navigateToSelfcareLoginModal = () => {
        switch (process.env.REACT_APP_ENV) {
            case ENV_LOCAL:
                navigate('/modal')
                break
            default:
                location.replace(`${HOME_LINK}?selfcareModal=true`)
                break
        }
    }

    const handleAcceptError = () => {
        deleteCart({
            _locale: language ?? DEFAULT_LANG,
            cartCreatorType: 'standard',
        }).then(() => {
            dispatch(setIsErrorModalOpened(false))
            window.location.replace(selfcareFirstStep)
        })
    }

    useEffect(() => {
        if (isCartFetching || !cartData) return
        const { cart } = cartData || {}
        const { items } = cart || {}

        dispatch(setSelfcareCart(cartData))

        if (items?.find((item) => item.productType === 'shipping')) {
            dispatch(setDeliveryType('shipping'))
        }
    }, [isCartFetching])

    useEffect(() => {
        if (isCartError && cartError) catchErrorByStatus({ error: cartError })
    }, [isCartError, cartError])

    useEffect(() => {
        if (arePositionsLoading) return

        if (isPositionsError && !isPathForLocalhostModal) {
            if (positionsError && catchErrorByStatus({ error: positionsError }))
                return

            dispatch(setIsSelfcareCartCreated(false))
            navigateToSelfcareLoginModal()
        } else if (positionsData) {
            dispatch(setIsSelfcareCartCreated(true))

            const { positions, orderId, sessionType } = positionsData
            positions && dispatch(setPositions(positions))
            orderId && dispatch(setOrderId(orderId))
            if (sessionType === SESSION_TYPE_MARCH_PROMO) {
                dispatch(setIsMarchPromoMode(true))
                setIsMarchPromoSession(true)
            }
        }
    }, [arePositionsLoading])

    useEffect(() => {
        if (isPathForLocalhostModal && !isDevelopmentMode)
            navigateToSelfcareLoginModal()
    }, [])

    return (arePositionsLoading || !isCartCreated) &&
        (!isPathForLocalhostModal || !isDevelopmentMode) ? (
        <FullPageLoader />
    ) : (
        <Page>
            {isMarchPromoSession ? <MarchPromoRoutes /> : <StandardRoutes />}
            <ServerErrorModal onClick={handleAcceptError} />
            <ConfirmationModal />
        </Page>
    )
}

export default Selfcare
